import { formatChileanDate } from '@/lib/dateUtils';
import { formatCLP } from '@/lib/format';
import { TaxDocumentActions } from '@/pages/pagos/TaxDocumentActions';
import { Alert, EmptyRow, SkeletonRows } from '@/ui';
import { DownloadIcon } from '@/ui/icons';

const COLS = 5; // Diligencia · Monto · Fecha de pago · Comprobante · Factura SII

// Presentational table for the payment receipts list — data-free and on safe
// imports only (no '@/lib' barrel: it drags recepthor-api/turnstile env
// validation, which crashes Storybook), so the story can drive it with
// fixture rows. Mirrors the DiligenciaDetalleVista pattern. Rows follow
// mapPayin's shape: { paymentId, rol, title, amount, paidAt, hasReceipt,
// taxDocument, creditNotes }; the download side effects stay in the container.
export function ComprobantesPagoTabla({
  rows,
  isLoading,
  isError,
  onDownload,
  onViewFactura,
  onDownloadFactura,
}) {
  return (
    <>
      {isError && (
        <div style={{ marginBottom: 16 }}>
          <Alert tone="error">
            No se pudo cargar el historial de pagos. Por favor, intenta
            nuevamente.
          </Alert>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table className="tbl billing-invoices-table">
          <colgroup>
            <col style={{ width: '30%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '22%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Diligencia</th>
              <th className="num">Monto</th>
              <th>Fecha de pago</th>
              <th>Comprobante</th>
              <th>Factura SII</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows rows={5} cols={COLS} />
            ) : rows.length === 0 ? (
              <EmptyRow
                span={COLS}
                title="Aún no hay pagos"
                hint="Cuando pagues una diligencia, su comprobante de pago aparecerá aquí."
              />
            ) : (
              rows.map((r) => (
                <tr key={r.paymentId} data-testid={`payin-row-${r.paymentId}`}>
                  <td className="tbl__primary">
                    <div className="tbl__id">{r.rol}</div>
                    <div style={{ fontWeight: 400, marginTop: 3 }}>
                      {r.title}
                    </div>
                  </td>
                  <td className="num mono">{formatCLP(r.amount) ?? '—'}</td>
                  <td>{formatChileanDate(r.paidAt) || '—'}</td>
                  <td>
                    <button
                      type="button"
                      className="t-btn t-btn--secondary t-btn--sm"
                      disabled={!r.hasReceipt}
                      onClick={() => onDownload(r)}
                      {...(!r.hasReceipt && {
                        title: 'Comprobante no disponible',
                        'aria-label': 'Comprobante no disponible',
                      })}
                      data-testid={`payin-receipt-${r.paymentId}`}
                    >
                      <DownloadIcon size={18} />
                      Comprobante
                    </button>
                  </td>
                  <td>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        alignItems: 'flex-start',
                      }}
                    >
                      <TaxDocumentActions
                        variant="button"
                        doc={r.taxDocument}
                        onView={(doc) => onViewFactura(doc, r.rol)}
                        onDownload={(doc) => onDownloadFactura(doc, r.rol)}
                        testId={`payin-factura-${r.paymentId}`}
                      />
                      {(r.creditNotes ?? []).map((nc) => (
                        <TaxDocumentActions
                          key={nc.id}
                          variant="button"
                          kind="nc"
                          doc={nc}
                          onView={(doc) => onViewFactura(doc, r.rol, 'nc')}
                          onDownload={(doc) =>
                            onDownloadFactura(doc, r.rol, 'nc')
                          }
                          testId={`payin-nc-${nc.id}`}
                        />
                      ))}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
